import React, { useEffect, useState } from 'react';
import { SubscriptionsHeader } from '@/components/subscriptions/SubscriptionsHeader';
import { SubscriptionsComparisonTable } from '@/components/subscriptions/SubscriptionsComparisonTable';
import { SubscriptionHistorySection } from '@/components/subscriptions/SubscriptionHistorySection';
import { SubscriptionLoadingSkeleton } from '@/components/subscriptions/SubscriptionLoadingSkeleton';
import { CurrentPlanCard } from '@/components/subscriptions/CurrentPlanCard';
import { LimitsUsageCard } from '@/components/subscriptions/LimitsUsageCard';
import { UpgradePromptCard } from '@/components/subscriptions/UpgradePromptCard';
import { useSponsorPlans } from '@/hooks/useSponsorPlans';
import { useSponsoredPlans } from '@/hooks/useSponsoredPlans';
import { useAuth } from '@/hooks/useAuth';
import { useAsaasPlanHistory } from '@/hooks/useAsaasPlanHistory';
import { debugLogger } from '@/utils/debugLogger';
import { AdaptiveViewportProvider } from '@/contexts/AdaptiveViewportContext';

import { BillingPeriod } from '@/types/plans';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { RefreshCw, ChevronDown } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
const Subscriptions = () => {
  const {
    profileData
  } = useAuth();
  const userRole = profileData?.role;
  
  const asaasPlanHistory = useAsaasPlanHistory();
  const { isLoading: asaasDataLoading, currentPlanInfo } = asaasPlanHistory;
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const isMobile = useIsMobile();

  // Verificar feedback de pagamento
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      toast.success('🎉 Pagamento realizado com sucesso! Seu plano foi ativado.');
    } else if (paymentStatus === 'cancelled') {
      toast.info('Pagamento cancelado. Você pode tentar novamente a qualquer momento.');
    }
  }, [searchParams]);

  // Função para refresh manual dos dados
  const handleManualRefresh = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    debugLogger.debug('subscriptions', 'Refresh manual solicitado pelo usuário');
    
    try {
      await queryClient.invalidateQueries({
        queryKey: ['asaasHistory']
      });
      await queryClient.invalidateQueries({
        queryKey: ['currentPlan']
      });
      await queryClient.invalidateQueries({
        queryKey: ['profile']
      });
      
      toast.success('Dados atualizados!');
      debugLogger.debug('subscriptions', 'Refresh manual concluído com sucesso');
    } catch (error) {
      debugLogger.error('[SUBSCRIPTIONS] Erro ao atualizar dados:', error);
      toast.error('Erro ao atualizar dados. Tente novamente.');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Recarregar dados sempre que voltar para a página
  useEffect(() => {
    const refreshData = async () => {
      if (profileData?.id) {
        setIsRefreshing(true);
        debugLogger.debug('subscriptions', 'Página carregada/retornada, atualizando dados...');
        try {
          // Invalidar todas as queries relacionadas ao Asaas
          await queryClient.invalidateQueries({
            queryKey: ['asaasHistory']
          });
          await queryClient.invalidateQueries({
            queryKey: ['currentPlan']
          });

          debugLogger.debug('subscriptions', 'Dados atualizados com sucesso');
        } catch (error) {
          debugLogger.error('[SUBSCRIPTIONS] Erro ao atualizar dados:', error);
        } finally {
          setIsRefreshing(false);
        }
      }
    };
    refreshData();
  }, [location.pathname, profileData?.id]); // Executa sempre que a rota mudar ou o usuário for identificado

  // Usar hook específico baseado no role do usuário
  const sponsorPlansData = useSponsorPlans();
  const sponsoredPlansData = useSponsoredPlans();

  // Determinar qual conjunto de dados usar
  const plansData = userRole === 'sponsor' ? sponsorPlansData : sponsoredPlansData;
  const {
    plans,
    currentPlan,
    upgradePlan,
    calculateSavings,
    isLoading
  } = plansData;
  const handleUpgrade = (planId: string, billingPeriod: BillingPeriod) => {
    upgradePlan({
      planId: planId as any,
      billingPeriod
    });
  };

  // Título específico baseado no role
  const getPageTitle = () => {
    if (userRole === 'sponsor') {
      return isMobile ? 'Planos Exclusivos' : 'Planos Exclusivos para Patrocinadores';
    }
    if (userRole === 'sponsored') {
      return isMobile ? 'Planos Premium' : 'Planos Premium para Patrocinados';
    }
    return 'Assinaturas';
  };
  
  const getPageDescription = () => {
    if (userRole === 'sponsor') {
      return isMobile ? 'Acesso VIP às melhores conexões' : 'Acesso VIP às melhores conexões premium';
    }
    if (userRole === 'sponsored') {
      return isMobile ? 'Destaque-se com elegância' : 'Destaque-se e conecte-se com elegância';
    }
    return 'Escolha a ideal para você';
  };
  return <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900">
      <SubscriptionsHeader title={getPageTitle()} description={getPageDescription()} />

      {/* Loading overlay quando está atualizando */}
      {isRefreshing && <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-10 flex items-center justify-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10 flex items-center gap-3">
            <RefreshCw className="h-5 w-5 text-luxury-pinkBright animate-spin" />
            <span className="text-white font-medium">Carregando dados do plano...</span>
          </div>
        </div>}

      {/* Upgrade Dialog */}
      {showUpgradeDialog && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex md:items-center md:justify-center">
          <div className="bg-gradient-to-br from-gray-900 to-black w-full md:h-auto md:max-w-5xl md:max-h-[90vh] md:rounded-xl md:border md:border-white/10 md:m-4 h-[100dvh] overflow-y-auto overscroll-contain">
              <div className="px-5 py-4 md:p-6 pb-safe">
                <div className="flex items-center justify-between mb-6">
                  {/* Desktop title */}
                  <h2 className="hidden md:block text-2xl font-bold text-white">
                    Escolha seu Plano
                  </h2>
                  {/* Mobile title - responsivo sem truncar */}
                  <h2 className="md:hidden text-base sm:text-lg font-bold text-white leading-tight max-w-[70%]">
                    Desbloqueie Conexões Ilimitadas
                  </h2>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowUpgradeDialog(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    ✕
                  </Button>
                </div>
                
                 <AdaptiveViewportProvider>
                  <SubscriptionsComparisonTable
                    plans={plans}
                    currentPlan={currentPlan}
                    onUpgrade={handleUpgrade}
                    calculateSavings={calculateSavings}
                    isLoading={isLoading}
                    userRole={userRole}
                  />
                </AdaptiveViewportProvider>
              </div>
            </div>
          </div>
        )}
      
      <div className="pt-[68px] pb-20 md:pb-6 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
          {asaasDataLoading ? (
            <SubscriptionLoadingSkeleton />
          ) : (
            <>
              {/* Current Plan Card with Refresh Button */}
              <div className="space-y-3">
                <div className="flex items-center justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleManualRefresh}
                    disabled={isRefreshing}
                    className="text-white/60 hover:text-white hover:bg-white/10"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                    {isRefreshing ? 'Atualizando...' : 'Atualizar dados'}
                  </Button>
                </div>
                
                <CurrentPlanCard
                  planType={currentPlan}
                  billingPeriod={currentPlanInfo?.billing_period}
                  expiresAt={currentPlanInfo?.current_plan_expires_at}
                  onUpgradeClick={() => setShowUpgradeDialog(true)}
                  showUpgradeButton={!currentPlan.includes('premium')}
                />
              </div>

              {/* Limits Usage Card */}
              <LimitsUsageCard />

              {/* Upgrade Prompt Card (only if not Infinity) */}
              {!currentPlan.includes('premium') && (
                <UpgradePromptCard
                  currentPlan={currentPlan}
                  userRole={userRole}
                  onUpgradeClick={() => setShowUpgradeDialog(true)}
                />
              )}

              {/* Collapsible History Section */}
              <Collapsible open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
                <CollapsibleTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="w-full flex items-center justify-between text-white hover:bg-white/5 py-4"
                  >
                    <span className="text-lg font-semibold">Histórico de Assinaturas</span>
                    <ChevronDown className={`h-5 w-5 transition-transform ${isHistoryOpen ? 'rotate-180' : ''}`} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SubscriptionHistorySection />
                </CollapsibleContent>
              </Collapsible>
              </>
            )}
        </div>
      </div>
    </div>;
};
export default Subscriptions;